import { useDispatch } from '@sloyd/store-base';
import Color from 'color';
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { invokeEvent, ROOT_MEDIA } from 'shared-base';

export function useCopyPaste() {
  const dispatch = useDispatch();
  const params = useParams();
  const { projectId, modelId } = params;

  useEffect(() => {
    const copy = (ev: any) => {
      if (!modelId) {
        return;
      }

      dispatch({
        type: 'EDITOR',
        verb: 'copy',
      });
    };

    const paste = (ev: any) => {
      if (modelId) return;

      try {
        const content = ev.clipboardData.getData('text');
        const json = JSON.parse(content);

        dispatch({
          type: 'EDITOR',
          verb: 'paste',
          id: modelId,
          payload: {
            json,
          },
        });
      } catch (err) {
        invokeEvent('sentry', { err });
      }
    };

    document.addEventListener('copy', copy);
    document.addEventListener('paste', paste);

    return () => {
      document.removeEventListener('copy', copy);
      document.removeEventListener('paste', paste);
    };
  }, [projectId, modelId]);
}

export function useEditorBackground(params: IHolodeckState) {
  const {
    backgroundColor = '#0e2158',
    backgroundIntensity = 1,
    backdropEnabled,
    backdropId,
  } = params;

  const style: React.CSSProperties = useMemo(() => {
    const intensity = Math.min(Math.max(backgroundIntensity, 0), 1); // Clamp between 0 and 1

    // Adjust the secondary color intensity using color package
    const secondaryColor = Color(backgroundColor)
      .darken(1 - intensity)
      .hex();

    const output: React.CSSProperties = {};

    if (!params.backdropEnabled) {
      output.background = `radial-gradient(circle, ${backgroundColor}, ${secondaryColor})`;
    } else {
      output.backgroundImage = `url(${ROOT_MEDIA}/backdrops/B-${lz(backdropId ?? '')}.jpg)`;
      output.backgroundSize = 'cover';
      output.backgroundPosition = 'center';
    }

    return output;
  }, [backgroundColor, backgroundIntensity, backdropEnabled, backdropId]);

  return style;
}

const lz = (number: string) => {
  return String(number).padStart(3, '0');
};
